import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Sparkles } from 'lucide-react';
import type { AddItem } from '../../types/actions';
import type { CampaignData, WikiArticle, WikiTemplate } from '../../../shared/types';
import TextAreaField from '../ui/TextAreaField';
import { api } from '../../api/client';
import { parseWikiDraftResponse } from '../wiki/aiDraftParser';
import MarkdownEditor from '../editor/MarkdownEditor';
import {
  getWikiTemplateByType,
  normalizeWikiTemplateData,
  validateWikiTemplateData,
} from '../wiki/templateUtils';
import {
  getVisibilityAudiencePreview,
  getVisibilityOptions,
  normalizeVisibility,
} from '../permissions/visibilityOptions';

export default function WikiArticleForm({
  addItem,
  articles,
  canSetGmVisibility = false,
  templates = [],
  submitRef,
  showSubmit = true,
  campaignData,
}: {
  addItem: AddItem;
  articles: WikiArticle[];
  canSetGmVisibility?: boolean;
  templates?: WikiTemplate[];
  submitRef?: React.MutableRefObject<(() => void) | null>;
  showSubmit?: boolean;
  campaignData?: CampaignData;
}) {
  const [article, setArticle] = useState({
    title: '',
    type: '',
    summary: '',
    content: '',
    parentId: null as number | null,
    tagsText: '',
    visibility: 'public',
    templateType: '',
    templateData: {} as Record<string, string>,
  });
  const [errors, setErrors] = useState<{
    title?: string;
    templateFields: Record<string, string>;
  }>({ templateFields: {} });
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState('');
  const [isDraftingWithAi, setIsDraftingWithAi] = useState(false);

  const parentOptions = useMemo(() => articles, [articles]);
  const selectedTemplate = useMemo(
    () => getWikiTemplateByType(templates, article.templateType),
    [templates, article.templateType]
  );
  const visibilityOptions = useMemo(
    () => getVisibilityOptions(canSetGmVisibility),
    [canSetGmVisibility]
  );

  const parseTags = useCallback(
    (value: string) =>
      value
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    []
  );

  const draftWithAi = useCallback(async () => {
    if (isDraftingWithAi) return;

    const prompt = aiPrompt.trim();
    const title = article.title.trim();
    const type = article.type.trim();

    if (!prompt && !title && !type) {
      setAiError('Add a title, type, or draft prompt before generating with AI.');
      return;
    }

    const templateGuide = selectedTemplate
      ? selectedTemplate.fields
          .map((field) => `${field.key}${field.required ? ' (required)' : ''}`)
          .join(', ')
      : 'none';

    const systemPrompt = `You draft campaign wiki content for tabletop GMs.
Return ONLY a compact JSON object with keys:
title, type, summary, content, tags, templateData.
Rules:
- Use markdown in content.
- Keep summary under 240 characters.
- tags must be an array of short strings.
- templateData must be an object with string values.
- Do not include any keys beyond the required schema.`;

    const userPrompt = `Build or refine a wiki article draft.
Current draft:
- title: ${title || '(empty)'}
- type: ${type || '(empty)'}
- summary: ${article.summary || '(empty)'}
- content: ${article.content || '(empty)'}
- tags: ${article.tagsText || '(empty)'}
- selected template fields: ${templateGuide}
- gm prompt: ${prompt || '(none)'}`;

    setIsDraftingWithAi(true);
    setAiError('');

    try {
      const result = await api.sendAssistantMessage(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        0.6
      );

      const parsed = parseWikiDraftResponse(result.content);
      if (!parsed) {
        throw new Error('AI returned an unexpected format. Try again with a more specific prompt.');
      }

      setArticle((previous) => ({
        ...previous,
        title: parsed.title || previous.title,
        type: parsed.type || previous.type,
        summary: parsed.summary || previous.summary,
        content: parsed.content || previous.content,
        tagsText: parsed.tags.length > 0 ? parsed.tags.join(', ') : previous.tagsText,
        templateData: selectedTemplate
          ? normalizeWikiTemplateData(selectedTemplate, {
              ...previous.templateData,
              ...parsed.templateData,
            })
          : previous.templateData,
      }));
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Failed to generate draft with AI.');
    } finally {
      setIsDraftingWithAi(false);
    }
  }, [
    aiPrompt,
    article.content,
    article.summary,
    article.tagsText,
    article.title,
    article.type,
    isDraftingWithAi,
    selectedTemplate,
  ]);

  const handleSubmit = useCallback(() => {
    const normalizedTemplateData = normalizeWikiTemplateData(
      selectedTemplate,
      article.templateData
    );
    const templateFieldErrors = validateWikiTemplateData(
      selectedTemplate,
      normalizedTemplateData
    );

    const nextErrors: {
      title?: string;
      templateFields: Record<string, string>;
    } = {
      templateFields: templateFieldErrors,
    };

    if (!article.title.trim()) {
      nextErrors.title = 'Title is required.';
    }

    if (nextErrors.title || Object.keys(nextErrors.templateFields).length > 0) {
      setErrors(nextErrors);
      return;
    }

    addItem('wikiArticles', {
      title: article.title.trim(),
      type: article.type.trim(),
      summary: article.summary,
      content: article.content,
      parentId: article.parentId,
      visibility: article.visibility,
      templateType: selectedTemplate?.key || '',
      templateData: normalizedTemplateData,
      tags: parseTags(article.tagsText),
      relationships: [],
      entityLinks: [],
    });

    setArticle({
      title: '',
      type: '',
      summary: '',
      content: '',
      parentId: null,
      tagsText: '',
      visibility: 'public',
      templateType: '',
      templateData: {},
    });
    setAiPrompt('');
    setAiError('');
    setErrors({ templateFields: {} });
  }, [addItem, article, parseTags, selectedTemplate]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
  }, [submitRef, handleSubmit]);

  return (
    <div className="bg-[var(--accent-muted)] p-4 rounded-lg border border-[var(--glass-border)] mb-4">
      <div className="field-block">
        <label className="field-label">Title</label>
        <input
          type="text"
          placeholder="Article title"
          value={article.title}
          onChange={(e) => {
            setArticle({ ...article, title: e.target.value });
            if (errors.title) {
              setErrors((prev) => ({ ...prev, title: undefined }));
            }
          }}
          className={`input-field ${errors.title ? 'input-error' : ''}`}
        />
        <div className="field-meta">
          <span className={errors.title ? 'field-error' : 'field-hint'}>
            {errors.title || 'Required.'}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="field-block">
          <label className="field-label">Type</label>
          <input
            type="text"
            placeholder="Faction, Region, Lore, Item..."
            value={article.type}
            onChange={(e) => setArticle({ ...article, type: e.target.value })}
            className="input-field"
          />
        </div>
        <div className="field-block">
          <label className="field-label">Parent Article</label>
          <select
            value={article.parentId ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setArticle({ ...article, parentId: value ? parseInt(value) : null });
            }}
            className="input-field"
          >
            <option value="">None</option>
            {parentOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field-block">
        <label className="field-label">Template</label>
        <select
          value={article.templateType}
          onChange={(e) => {
            const nextTemplate = getWikiTemplateByType(templates, e.target.value);
            const nextTemplateData = normalizeWikiTemplateData(
              nextTemplate,
              article.templateData
            );
            setArticle({
              ...article,
              templateType: nextTemplate?.key || '',
              templateData: nextTemplateData,
            });
            setErrors((prev) => ({ ...prev, templateFields: {} }));
          }}
          className="input-field"
        >
          <option value="">None</option>
          {templates.map((template) => (
            <option key={template.key} value={template.key}>
              {template.label}
            </option>
          ))}
        </select>
        <div className="field-meta">
          <span className="field-hint">
            {selectedTemplate
              ? selectedTemplate.description
              : 'Optional structured metadata for common article types.'}
          </span>
        </div>
      </div>
      {selectedTemplate && selectedTemplate.fields.length > 0 && (
        <div className="field-block">
          <label className="field-label">Template Fields</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedTemplate.fields.map((field) => {
              const value = article.templateData[field.key] || '';
              const fieldError = errors.templateFields[field.key];
              return (
                <div className="field-block" key={field.key}>
                  <label className="field-label">
                    {field.label}
                    {field.required ? ' *' : ''}
                  </label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setArticle((prev) => ({
                        ...prev,
                        templateData: {
                          ...prev.templateData,
                          [field.key]: nextValue,
                        },
                      }));
                      if (fieldError) {
                        setErrors((prev) => {
                          const nextTemplateErrors = { ...prev.templateFields };
                          delete nextTemplateErrors[field.key];
                          return {
                            ...prev,
                            templateFields: nextTemplateErrors,
                          };
                        });
                      }
                    }}
                    className={`input-field ${fieldError ? 'input-error' : ''}`}
                    placeholder={field.label}
                  />
                  {fieldError && (
                    <div className="field-meta">
                      <span className="field-error">{fieldError}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="field-block">
        <label className="field-label">Tags</label>
        <input
          type="text"
          placeholder="Comma-separated tags"
          value={article.tagsText}
          onChange={(e) => setArticle({ ...article, tagsText: e.target.value })}
          className="input-field"
        />
      </div>
      <div className="field-block">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="field-label mb-0">AI Draft Assistant</label>
          <button
            type="button"
            onClick={() => void draftWithAi()}
            disabled={isDraftingWithAi}
            className={`btn-primary-solid flex items-center gap-2 ${isDraftingWithAi ? 'opacity-70 pointer-events-none' : ''}`}
          >
            {isDraftingWithAi ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {isDraftingWithAi ? 'Generating...' : 'Draft With AI'}
          </button>
        </div>
        <textarea
          value={aiPrompt}
          onChange={(event) => {
            setAiPrompt(event.target.value);
            if (aiError) setAiError('');
          }}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault();
              void draftWithAi();
            }
          }}
          className="input-field min-h-[84px]"
          placeholder="Optional: describe tone, canon constraints, perspective, or what this article must accomplish."
        />
        <div className="field-meta">
          <span className={aiError ? 'field-error' : 'field-hint'}>
            {aiError || 'Shortcut: Cmd/Ctrl + Enter to generate.'}
          </span>
        </div>
      </div>
      <TextAreaField
        label="Summary"
        placeholder="Short summary..."
        value={article.summary}
        onChange={(value) => setArticle({ ...article, summary: value })}
        hint="Quick overview for lists and search results."
        showCount
        minRows={3}
      />
      <div className="field-block">
        <label className="field-label">Content</label>
        <MarkdownEditor
          value={article.content}
          onChange={(val) => setArticle(prev => ({ ...prev, content: val }))}
          placeholder="Full article content (Markdown supported, use [[Title]] for wiki links)"
          minHeight="200px"
          campaignData={campaignData}
        />
        <div className="field-meta">
          <span className="field-hint">Use toolbar for formatting. Type [[ to link entities.</span>
          <span className="field-hint">{article.content.length}</span>
        </div>
      </div>
      <div className="field-block">
        <label className="field-label">Visibility</label>
        <select
          value={normalizeVisibility(article.visibility)}
          onChange={(e) => setArticle({ ...article, visibility: e.target.value })}
          className="input-field"
        >
          {visibilityOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="field-meta">
          <span className="field-hint">
            {getVisibilityAudiencePreview(article.visibility)}
          </span>
        </div>
        {canSetGmVisibility && normalizeVisibility(article.visibility) === 'share_group' && (
          <div className="field-meta">
            <span className="field-hint">
              After saving, link at least one share group in the article card to grant player access.
            </span>
          </div>
        )}
      </div>
      {showSubmit && (
        <button
          onClick={handleSubmit}
          className="btn-primary-solid flex items-center gap-2"
        >
          <Plus size={16} /> Add Article
        </button>
      )}
    </div>
  );
}
